import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Button } from "@/components/ui/button"
import { Switch } from "@/components/ui/switch"
import { RefreshCw, Database, Settings, ChevronDown, ChevronUp } from "lucide-react"
import { DatasourceBillsTable } from '@/components/DatasourceBillsTable'
import { useDatasourceBills } from '@/hooks/bills/useDatasourceBills'
import { useSyncSettings } from '@/hooks/admin/useSyncSettings'
import { useToast } from '@/hooks/use-toast'
import { SessionDisplay } from '@/components/SessionDisplay'
import { useState } from 'react'
import type { APISession } from '@/types/session'

const API_SESSIONS: APISession[] = ['2023RS', '2024RS', '2025RS']
const DATA_SOURCES = ['Maryland', 'Federal'] as const
type DataSource = typeof DATA_SOURCES[number]

export default function DatasourceBillsPage() {
  const { toast } = useToast();
  
  // Main data source selection state
  const [selectedDataSource, setSelectedDataSource] = useState<DataSource>('Maryland');
  
  // Collapsible sections state
  const [isSyncSettingsExpanded, setIsSyncSettingsExpanded] = useState(false);

  // Sync settings hook
  const {
    data: syncSettings,
    updateSyncSettings,
    isPending: isMutating,
  } = useSyncSettings();

  // Bills data hook
  const {
    bills,
    isLoading,
    selectedSession: billsSelectedSession,
    setSelectedDataSource: setBillsDataSource,
    setSelectedSession: setBillsSelectedSession,
    currentPage,
    pageSize,
    totalPages,
    handlePageChange,
    searchQuery,
    setSearchQuery,
    totalFilteredItems,
    sortField,
    sortDirection,
    setSorting,
    refetch
  } = useDatasourceBills('Maryland', '2024RS')

  // Sync the bills data source with the main selection
  const handleDataSourceChange = (dataSource: DataSource) => {
    setSelectedDataSource(dataSource);
    setBillsDataSource(dataSource);
  }

  const handleSessionChange = (value: string) => {
    if (API_SESSIONS.includes(value as APISession)) {
      setBillsSelectedSession(value as APISession)
    }
  }

  const handleRefresh = () => {
    refetch()
  }

  const handleSyncToggle = (session: APISession, enabled: boolean) => {
    const currentSessionSync = syncSettings?.session_sync || {
      maryland: {},
      federal: {}
    };
    const dataSourceKey = selectedDataSource.toLowerCase() as 'maryland' | 'federal';
    
    // Create updated session sync settings keyed by data source
    const updatedSessionSync = {
      ...currentSessionSync,
      [dataSourceKey]: {
        ...currentSessionSync[dataSourceKey],
        [session]: enabled
      }
    };

    console.log('Toggle debug:', {
      session,
      enabled,
      dataSourceKey,
      currentSessionSync,
      updatedSessionSync
    });

    updateSyncSettings({
      session_sync: updatedSessionSync
    });
    
    toast({
      title: enabled ? `${selectedDataSource} Sync Enabled` : `${selectedDataSource} Sync Disabled`,
      description: `${session} ${selectedDataSource} bill sync has been ${enabled ? 'enabled' : 'disabled'}`
    });
  };

  const isSessionSyncEnabled = (session: APISession): boolean => {
    const dataSourceKey = selectedDataSource.toLowerCase() as 'maryland' | 'federal';
    return syncSettings?.session_sync?.[dataSourceKey]?.[session] || false;
  };

  const getDataSourceIcon = (dataSource: DataSource) => {
    return dataSource === 'Maryland' ? '🏛️' : '🇺🇸';
  };


  return (
    <div className="container mx-auto p-2 sm:p-4 md:p-6 space-y-6">
      {/* Data Source Selection */}
      <Card className="border shadow-sm">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Database className="h-4 w-4" />
              Data Source:
            </CardTitle>
            <Select
              value={selectedDataSource}
              onValueChange={handleDataSourceChange}
            >
              <SelectTrigger className="w-[160px] h-8">
                <SelectValue>
                  <div className="flex items-center gap-1">
                    <span>{getDataSourceIcon(selectedDataSource)}</span>
                    {selectedDataSource}
                  </div>
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {DATA_SOURCES.map((source) => (
                  <SelectItem key={source} value={source}>
                    <div className="flex items-center gap-1">
                      <span>{getDataSourceIcon(source)}</span>
                      {source}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
      </Card>

      {/* Sync Settings Card */}
      <Card className="border shadow-sm">
        <CardHeader 
          className="pb-2 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50"
          onClick={() => setIsSyncSettingsExpanded(!isSyncSettingsExpanded)}
        >
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Settings className="h-4 w-4" />
              {selectedDataSource} Sync Settings
              <span className="text-sm font-normal text-slate-500">
                ({API_SESSIONS.filter(session => isSessionSyncEnabled(session)).length}/{API_SESSIONS.length} enabled)
              </span>
            </CardTitle>
            <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
              {isSyncSettingsExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
          </div>
        </CardHeader>
        {isSyncSettingsExpanded && (
          <CardContent className="pt-0 pb-3">
            <div className="space-y-2">
              {API_SESSIONS.map((session) => (
                <div 
                  key={session} 
                  className="flex items-center justify-between p-2 rounded border hover:bg-slate-50 dark:hover:bg-slate-800/50"
                >
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${isSessionSyncEnabled(session) ? 'bg-green-500' : 'bg-slate-300'}`}></div>
                    <SessionDisplay session={session} />
                  </div>
                  
                  <Switch
                    id={`${session}-${selectedDataSource.toLowerCase()}`}
                    checked={isSessionSyncEnabled(session)}
                    onCheckedChange={(enabled) => handleSyncToggle(session, enabled)}
                    disabled={isMutating}
                  />
                </div>
              ))}
            </div>
            
            {syncSettings?.updated_at && (
              <div className="mt-3 pt-2 border-t">
                <p className="text-xs text-slate-500 flex items-center gap-1">
                  <div className="w-1 h-1 rounded-full bg-green-500"></div>
                  Updated by {syncSettings.updated_by} at {new Date(syncSettings.updated_at).toLocaleString()}
                </p>
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* Datasource Bills Card */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-4">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 sm:gap-0">
              <div>
                <CardTitle className="text-xl flex items-center gap-3 text-slate-800 dark:text-slate-200">
                  <div className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-900/30">
                    <span className="text-lg">{getDataSourceIcon(selectedDataSource)}</span>
                  </div>
                  {selectedDataSource} Bills
                </CardTitle>
                <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                  View raw bill data from {selectedDataSource} legislative data source
                  {billsSelectedSession && (
                    <span className="ml-2 px-2 py-1 bg-slate-100 dark:bg-slate-700 rounded-md text-xs font-medium">
                      {billsSelectedSession}
                    </span>
                  )}
                </p>
              </div>
              <div className="flex gap-3 items-center w-full sm:w-auto">
                <Select
                  value={billsSelectedSession || undefined}
                  onValueChange={handleSessionChange}
                >
                  <SelectTrigger className="w-[140px] h-9 border-slate-300 dark:border-slate-600">
                    <SelectValue placeholder="Session">
                      {billsSelectedSession && <SessionDisplay session={billsSelectedSession} />}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {API_SESSIONS.map((session) => (
                      <SelectItem key={session} value={session}>
                        <SessionDisplay session={session} />
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRefresh}
                  disabled={isLoading}
                  className="flex items-center gap-2 h-9 px-3 border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-800"
                >
                  <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {billsSelectedSession ? (
            <DatasourceBillsTable
              bills={bills}
              currentPage={currentPage}
              pageSize={pageSize}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              isLoading={isLoading}
              totalFilteredItems={totalFilteredItems}
              sortField={sortField}
              sortDirection={sortDirection}
              onSortChange={setSorting}
            />
          ) : (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="p-4 rounded-full bg-slate-100 dark:bg-slate-800 mb-4">
                <span className="text-3xl">{getDataSourceIcon(selectedDataSource)}</span>
              </div>
              <p className="text-lg font-medium text-slate-700 dark:text-slate-300 mb-2">
                Select Legislative Session
              </p>
              <p className="text-sm text-slate-500 dark:text-slate-400 text-center max-w-md">
                Choose a legislative session to view raw bill data from the {selectedDataSource} data source.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}